/**
 * CarerProfiles — Family-facing "Browse Carer Profiles" (v3, compact cards).
 *
 * Read-only discovery directory of currently APPROVED, publicly-visible
 * Carer Profiles (both tiers) via GET /api/carer-profile/published — a
 * family-safe projection (see `public_profile_view` in care_profiles.py)
 * that never includes contact details (email/phone), preferred_contact
 * methods or any moderation-only field.
 *
 * Cards here are intentionally COMPACT previews only (photo, first name,
 * area, membership badge, main care type, a short About Me excerpt,
 * availability, indicative rate) so the directory stays browsable as
 * more carers join. Every field, plus the enquiry/invite actions, still
 * lives in full on the dedicated profile page at
 * /family/carer-profiles/:profileId (see CarerProfileDetail.jsx) —
 * nothing was removed or shortened there, only from this browse view.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users2, CalendarClock, Wallet } from "lucide-react";
import { api } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";

function Chip({ children }) {
  return <span className="pill bg-slate-100 text-ink text-xs">{children}</span>;
}

function ProfilePreviewCard({ p }) {
  const firstName = (p.name || "").trim().split(/\s+/)[0] || p.name;
  const mainCareType = (p.care_types || [])[0];

  return (
    <Link
      to={`/family/carer-profiles/${p.profile_id}`}
      className="card-base p-5 flex flex-col text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
      data-testid={`carer-profile-card-${p.profile_id}`}
    >
      <div className="flex items-start gap-3">
        {p.photo_data_url ? (
          <img
            src={p.photo_data_url}
            alt=""
            className="w-14 h-14 rounded-full object-cover border border-slate-200 shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-purple/10 text-purple flex items-center justify-center text-[10px] font-semibold shrink-0 text-center">
            No photo
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-semibold truncate" data-testid={`carer-profile-name-${p.profile_id}`}>
              {firstName}
            </div>
          </div>
          {p.general_location && (
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-muted">
              <MapPin className="w-3.5 h-3.5 text-purple shrink-0" strokeWidth={1.75} />
              <span className="truncate">{p.general_location}</span>
            </div>
          )}
        </div>
      </div>

      {mainCareType && (
        <div className="mt-3">
          <Chip>{mainCareType}</Chip>
        </div>
      )}

      {p.about_you && (
        <p
          className="mt-3 text-sm text-ink-muted leading-relaxed overflow-hidden"
          style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3 }}
          data-testid={`carer-profile-excerpt-${p.profile_id}`}
        >
          {p.about_you}
        </p>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-ink-muted flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />Availability</div>
          <div className="mt-0.5 font-medium truncate" data-testid={`carer-profile-availability-${p.profile_id}`}>{p.availability_status || "—"}</div>
        </div>
        <div>
          <div className="text-ink-muted flex items-center gap-1"><Wallet className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />Indicative rate</div>
          <div className="mt-0.5 font-medium truncate">{p.indicative_rates || "—"}</div>
        </div>
      </div>

      <span className="btn-outline text-sm mt-4 w-full justify-center" data-testid={`carer-profile-view-btn-${p.profile_id}`}>
        View Full Profile
      </span>
    </Link>
  );
}

export default function CarerProfiles() {
  const [profiles, setProfiles] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/carer-profile/published");
        if (alive) setProfiles(data.profiles || []);
      } catch {
        if (alive) setError(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="page-carer-profiles">
      <PageHero
        eyebrow="Carer Profiles"
        title="Browse Carer Profiles"
        description="Approved Carer Profiles from independent carers on Just Care Community. Open a profile to see full details, send a private enquiry or invite a carer to view one of your Care Needs — no contact details are shared until you both choose to progress."
      >
        <Link to="/family/care-needs" className="btn-outline text-sm" data-testid="carer-profiles-back-care-needs">
          Post a care need
        </Link>
      </PageHero>

      <section className="section pt-8 pb-20">
        <div className="container-jcc max-w-6xl">
          {profiles === null && !error && (
            <p className="text-ink-muted" data-testid="carer-profiles-loading">Loading carer profiles…</p>
          )}
          {error && (
            <p className="text-ink-muted" data-testid="carer-profiles-error">
              We couldn't load carer profiles right now. Please try again shortly.
            </p>
          )}
          {profiles !== null && profiles.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="carer-profiles-empty">
              <div className="w-12 h-12 rounded-xl bg-purple/10 text-purple flex items-center justify-center mx-auto mb-4">
                <Users2 className="w-6 h-6" strokeWidth={1.75} />
              </div>
              <h2 className="font-display font-semibold text-lg">No carer profiles are published yet</h2>
              <p className="mt-2 text-ink-muted max-w-md mx-auto">
                Approved Carer Profiles will appear here as carers complete and JCC reviews them —
                please check back soon.
              </p>
            </div>
          )}
          {profiles !== null && profiles.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="carer-profiles-list">
              {profiles.map((p) => <ProfilePreviewCard key={p.profile_id} p={p} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
